/**
 * Browser History Manager
 *
 * Utilities for managing browser history state, scroll position restoration,
 * and view state persistence across navigation.
 *
 * Uses `window.history.state` and `window.history.replaceState()` to persist
 * UI state without creating additional history entries.
 *
 * @module shared/utils/routing/historyManager
 */

import type { ViewState } from '@shared/types/routing';

/**
 * Key for storing view state in history state object
 */
const VIEW_STATE_KEY = 'bo360_view_state';

/**
 * Key for storing scroll position in history state object
 */
const SCROLL_POSITION_KEY = 'bo360_scroll_position';

/**
 * Maximum number of view states to keep in memory
 * Prevents memory leaks from accumulating too many states
 */
const MAX_VIEW_STATES = 50;

/**
 * In-memory cache of view states by history state key
 * Used as fallback when history state is not available
 */
const viewStateCache = new Map<string, ViewState>();

/**
 * Save view state to browser history
 *
 * Stores view state in the current history entry without creating a new entry.
 * Useful for preserving UI state (filters, tabs, expanded sections) across
 * forward/back navigation.
 *
 * @param key - Unique key for this view state (e.g., route path or component ID)
 * @param state - View state object to save
 *
 * @example
 * ```typescript
 * // Save current filter state
 * saveViewState('organizations-list', {
 *   filters: { status: 'active', tags: ['sales'] },
 *   activeTab: 'details',
 *   scrollY: 450
 * });
 * ```
 */
export function saveViewState(key: string, state: ViewState): void {
  try {
    // Get current history state or create new one
    const currentState = window.history.state || {};

    // Merge new view state with existing state
    const viewStates = currentState[VIEW_STATE_KEY] || {};
    viewStates[key] = {
      ...state,
      timestamp: Date.now(), // Track when state was saved
    };

    // Update history state without creating new entry
    window.history.replaceState(
      {
        ...currentState,
        [VIEW_STATE_KEY]: viewStates,
      },
      ''
    );

    // Update in-memory cache as fallback
    viewStateCache.set(key, state);

    // Limit cache size to prevent memory leaks
    if (viewStateCache.size > MAX_VIEW_STATES) {
      const firstKey = viewStateCache.keys().next().value;
      if (firstKey) {
        viewStateCache.delete(firstKey);
      }
    }
  } catch (error) {
    console.error('[HistoryManager] Failed to save view state:', error);
  }
}

/**
 * Restore view state from browser history
 *
 * Retrieves previously saved view state from the current history entry.
 * Returns null if no state exists for the given key.
 *
 * @param key - Unique key for the view state to restore
 * @returns Saved view state or null if not found
 *
 * @example
 * ```typescript
 * // Restore filter state on component mount
 * useEffect(() => {
 *   const savedState = restoreViewState('organizations-list');
 *   if (savedState) {
 *     setFilters(savedState.filters);
 *     setActiveTab(savedState.activeTab);
 *     if (savedState.scrollY) {
 *       window.scrollTo(0, savedState.scrollY);
 *     }
 *   }
 * }, []);
 * ```
 */
export function restoreViewState(key: string): ViewState | null {
  try {
    // Try to get from history state first
    const currentState = window.history.state;
    if (currentState?.[VIEW_STATE_KEY]?.[key]) {
      return currentState[VIEW_STATE_KEY][key];
    }

    // Fallback to in-memory cache
    const cached = viewStateCache.get(key);
    return cached || null;
  } catch (error) {
    console.error('[HistoryManager] Failed to restore view state:', error);
    return null;
  }
}

/**
 * Clear view state for a specific key
 *
 * @param key - Key of view state to clear
 *
 * @example
 * ```typescript
 * // Clear saved state on logout
 * clearViewState('organizations-list');
 * ```
 */
export function clearViewState(key: string): void {
  try {
    const currentState = window.history.state || {};
    const viewStates = currentState[VIEW_STATE_KEY] || {};

    delete viewStates[key];

    window.history.replaceState(
      {
        ...currentState,
        [VIEW_STATE_KEY]: viewStates,
      },
      ''
    );

    // Also clear from cache
    viewStateCache.delete(key);
  } catch (error) {
    console.error('[HistoryManager] Failed to clear view state:', error);
  }
}

/**
 * Clear all view states
 *
 * Useful for cleanup on logout or session reset.
 *
 * @example
 * ```typescript
 * // Clear all saved states on logout
 * clearAllViewStates();
 * ```
 */
export function clearAllViewStates(): void {
  try {
    const currentState = window.history.state || {};

    window.history.replaceState(
      {
        ...currentState,
        [VIEW_STATE_KEY]: {},
      },
      ''
    );

    viewStateCache.clear();
  } catch (error) {
    console.error('[HistoryManager] Failed to clear all view states:', error);
  }
}

/**
 * Save current scroll position to browser history
 *
 * Stores both vertical and horizontal scroll positions for the current page.
 * Useful for preserving scroll position across forward/back navigation.
 *
 * @example
 * ```typescript
 * // Save scroll position before navigation
 * useEffect(() => {
 *   const handleBeforeUnload = () => {
 *     saveScrollPosition();
 *   };
 *
 *   window.addEventListener('beforeunload', handleBeforeUnload);
 *   return () => window.removeEventListener('beforeunload', handleBeforeUnload);
 * }, []);
 * ```
 */
export function saveScrollPosition(): void {
  try {
    const currentState = window.history.state || {};

    window.history.replaceState(
      {
        ...currentState,
        [SCROLL_POSITION_KEY]: {
          x: window.scrollX,
          y: window.scrollY,
          timestamp: Date.now(),
        },
      },
      ''
    );
  } catch (error) {
    console.error('[HistoryManager] Failed to save scroll position:', error);
  }
}

/**
 * Restore scroll position from browser history
 *
 * Restores previously saved scroll position for the current page.
 * Returns early if no saved position exists.
 *
 * @param behavior - Scroll behavior ('auto' | 'smooth', default: 'auto')
 *
 * @example
 * ```typescript
 * // Restore scroll position on component mount
 * useEffect(() => {
 *   // Use timeout to ensure DOM is fully rendered
 *   const timer = setTimeout(() => {
 *     restoreScrollPosition('smooth');
 *   }, 100);
 *
 *   return () => clearTimeout(timer);
 * }, []);
 * ```
 */
export function restoreScrollPosition(behavior: ScrollBehavior = 'auto'): void {
  try {
    const currentState = window.history.state;
    const scrollPosition = currentState?.[SCROLL_POSITION_KEY];

    if (scrollPosition) {
      window.scrollTo({
        left: scrollPosition.x,
        top: scrollPosition.y,
        behavior,
      });
    }
  } catch (error) {
    console.error('[HistoryManager] Failed to restore scroll position:', error);
  }
}

/**
 * Get saved scroll position without restoring
 *
 * @returns Saved scroll position or null if not found
 *
 * @example
 * ```typescript
 * const scrollPos = getScrollPosition();
 * if (scrollPos && scrollPos.y > 500) {
 *   showScrollToTopButton();
 * }
 * ```
 */
export function getScrollPosition(): { x: number; y: number } | null {
  try {
    const currentState = window.history.state;
    return currentState?.[SCROLL_POSITION_KEY] || null;
  } catch (error) {
    console.error('[HistoryManager] Failed to get scroll position:', error);
    return null;
  }
}

/**
 * Clear saved scroll position
 *
 * @example
 * ```typescript
 * // Clear scroll position when user manually scrolls
 * clearScrollPosition();
 * ```
 */
export function clearScrollPosition(): void {
  try {
    const currentState = window.history.state || {};

    window.history.replaceState(
      {
        ...currentState,
        [SCROLL_POSITION_KEY]: null,
      },
      ''
    );
  } catch (error) {
    console.error('[HistoryManager] Failed to clear scroll position:', error);
  }
}

/**
 * Auto-save scroll position on interval
 *
 * Returns a cleanup function to stop auto-saving.
 *
 * @param intervalMs - Interval in milliseconds (default: 1000ms)
 * @returns Cleanup function to stop auto-saving
 *
 * @example
 * ```typescript
 * // Auto-save scroll position every 2 seconds
 * useEffect(() => {
 *   const cleanup = autoSaveScrollPosition(2000);
 *   return cleanup;
 * }, []);
 * ```
 */
export function autoSaveScrollPosition(intervalMs = 1000): () => void {
  const interval = setInterval(() => {
    saveScrollPosition();
  }, intervalMs);

  return () => clearInterval(interval);
}

/**
 * React hook for automatic scroll position management
 *
 * Automatically saves scroll position on unmount and restores on mount.
 *
 * @param options - Configuration options
 * @returns Object with manual save/restore functions
 *
 * @example
 * ```typescript
 * function OrganizationList() {
 *   useScrollPositionManager({
 *     autoRestore: true,
 *     autoSave: true,
 *     restoreDelay: 100
 *   });
 *
 *   // Component will automatically save/restore scroll position
 * }
 * ```
 */
export function useScrollPositionManager(options?: {
  /** Auto-restore scroll position on mount (default: true) */
  autoRestore?: boolean;
  /** Auto-save scroll position on unmount (default: true) */
  autoSave?: boolean;
  /** Delay before restoring scroll position in ms (default: 100) */
  restoreDelay?: number;
  /** Scroll behavior when restoring (default: 'auto') */
  behavior?: ScrollBehavior;
}): {
  save: () => void;
  restore: () => void;
  clear: () => void;
} {
  const {
    autoRestore = true,
    autoSave = true,
    restoreDelay = 100,
    behavior = 'auto',
  } = options || {};

  // Auto-restore on mount
  React.useEffect(() => {
    if (autoRestore) {
      const timer = setTimeout(() => {
        restoreScrollPosition(behavior);
      }, restoreDelay);

      return () => clearTimeout(timer);
    }
  }, [autoRestore, restoreDelay, behavior]);

  // Auto-save on unmount
  React.useEffect(() => {
    if (autoSave) {
      return () => {
        saveScrollPosition();
      };
    }
  }, [autoSave]);

  return {
    save: saveScrollPosition,
    restore: () => restoreScrollPosition(behavior),
    clear: clearScrollPosition,
  };
}

/**
 * React hook for automatic view state management
 *
 * Automatically saves view state on changes and restores on mount.
 *
 * @param key - Unique key for this view state
 * @param state - Current view state to save
 * @param options - Configuration options
 *
 * @example
 * ```typescript
 * function OrganizationList() {
 *   const [filters, setFilters] = useState({});
 *   const [activeTab, setActiveTab] = useState('details');
 *
 *   useViewStateManager('organizations-list', {
 *     filters,
 *     activeTab,
 *     scrollY: window.scrollY
 *   }, {
 *     autoRestore: true,
 *     onRestore: (state) => {
 *       setFilters(state.filters);
 *       setActiveTab(state.activeTab);
 *     }
 *   });
 * }
 * ```
 */
export function useViewStateManager(
  key: string,
  state: ViewState,
  options?: {
    /** Auto-restore view state on mount (default: true) */
    autoRestore?: boolean;
    /** Callback when view state is restored */
    onRestore?: (state: ViewState) => void;
    /** Debounce delay for auto-save in ms (default: 500) */
    debounceMs?: number;
  }
): {
  restore: () => ViewState | null;
  clear: () => void;
} {
  const { autoRestore = true, onRestore, debounceMs = 500 } = options || {};

  // Auto-restore on mount
  React.useEffect(() => {
    if (autoRestore) {
      const restored = restoreViewState(key);
      if (restored && onRestore) {
        onRestore(restored);
      }
    }
  }, [key, autoRestore, onRestore]);

  // Auto-save on state changes (debounced)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      saveViewState(key, state);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [key, state, debounceMs]);

  return {
    restore: () => restoreViewState(key),
    clear: () => clearViewState(key),
  };
}

// Import React for hooks
import React from 'react';
